import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { SitePopup } from "../SitePopup/SitePopup";

type QuoteField = HTMLInputElement | HTMLTextAreaElement;

@Component
export class EnquiryPopup extends ThemeComponent {
    @Ref modalContent!: HTMLElement;
    @Ref modalTrigger!: HTMLButtonElement;
    @Ref closeModal!: HTMLButtonElement;
    @Ref({ required: false }) quoteForm!: HTMLFormElement;
    @Ref({ required: false }) quoteErrors!: HTMLElement;
    @Ref({ required: false }) quoteSubmit!: HTMLButtonElement;
    @Ref({ required: false }) quoteSuccess!: HTMLTemplateElement;

    private sitePopup: SitePopup|null = document.querySelector('site-popup');
    private nativeSubmit = false;

    init() {
        this.sitePopup = document.querySelector('.site-popup') as SitePopup;

        this.$listen(this.modalTrigger, 'click', (e) => {
            e.preventDefault();
            this.openModal();
        });

        this.$listen(this.closeModal, 'click', () => {
            this.sitePopup?.close();
        });

        if (this.quoteForm) {
            this.initQuoteForm();
        }
    }

    private openModal() {
        const sitePopupContent = this.sitePopup?.querySelector('.site-popup__content');
        sitePopupContent?.append(this.modalContent);
        if (!this.sitePopup) { return; }
        this.sitePopup.open('find-a-box');
    }

    private initQuoteForm() {
        const posted = !this.quoteSubmit;
        const hasErrors = this.quoteErrors && !this.quoteErrors.hidden;
        if (posted || hasErrors) {
            this.openModal();
        }
        if (posted) { return; }

        this.quoteForm.noValidate = true;
        this.$listen(this.quoteForm, 'submit', (e) => {
            if (this.nativeSubmit) { return; }
            e.preventDefault();
            e.stopPropagation();
            this.submitQuote();
        });
    }

    private async submitQuote() {
        if (this.quoteSubmit.disabled || !this.validateQuote()) { return; }

        this.quoteSubmit.disabled = true;
        this.quoteSubmit.textContent = 'Submitting...';

        const emptyFields = Array.from(this.quoteForm.querySelectorAll<QuoteField>('.esc--field'))
            .filter((field) => !field.value.trim());
        const data = new FormData(this.quoteForm);
        emptyFields.forEach((field) => data.delete(field.name));

        const body = new URLSearchParams(data as unknown as Record<string, string>);
        const posted = await fetch(this.quoteForm.action, { method: 'POST', body })
            .then((response) => new URL(response.url).searchParams.get('contact_posted') === 'true')
            .catch(() => false);

        if (posted) {
            this.quoteForm.innerHTML = this.quoteSuccess.innerHTML;
            return;
        }

        emptyFields.forEach((field) => { field.disabled = true; });
        this.nativeSubmit = true;
        this.quoteForm.requestSubmit();
    }

    private validateQuote(): boolean {
        const fields = Array.from(this.quoteForm.querySelectorAll<QuoteField>('.esc--field'));
        let firstInvalid: QuoteField | null = null;

        for (const field of fields) {
            const message = this.quoteFieldError(field);
            this.showQuoteFieldError(field, message);
            if (message && !firstInvalid) {
                firstInvalid = field;
            }
        }

        this.quoteErrors.hidden = !firstInvalid;
        firstInvalid?.focus();
        return !firstInvalid;
    }

    private quoteFieldError(field: QuoteField): string | null {
        if (field.required && !field.value.trim()) {
            return field.dataset.requiredMessage || null;
        }
        if (field.type === 'email' && field.validity.typeMismatch) {
            return field.dataset.emailMessage || null;
        }
        return null;
    }

    private showQuoteFieldError(field: QuoteField, message: string | null) {
        const label = this.quoteForm.querySelector(`label[for="${field.id}"]`);
        if (!label) { return; }

        label.querySelector('.esc--form-errors')?.remove();

        if (message) {
            const error = document.createElement('span');
            error.className = 'esc--form-errors';
            error.textContent = message;
            label.append(error);
        }
    }
}
